import type { ReportKpi, ReportValue, Week } from 'chery-contracts';
import { Table, TableCell, TableHead, TableHeaderCell, TableRow } from '../../components/ui/Table';
import { cn } from '../../lib/cn';
import { PILLAR_CLASSES } from '../../lib/pillars';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { reportValueText, weekShortLabel } from './reports-display';

export interface DataTableProps {
  kpis: readonly ReportKpi[];
  weeks: readonly Pick<Week, 'id' | 'weekDate' | 'weekNumber'>[];
  values: Readonly<Record<string, ReportValue>>;
}

/** The weekly values behind the charts: one row per KPI, one column per week of the range; blanks are `—`. */
export const DataTable = ({ kpis, weeks, values }: DataTableProps) => {
  const { t } = useI18nTranslation();

  return (
    <Table
      data-data-table
      className="text-xs"
    >
      <TableHead>
        <tr>
          <TableHeaderCell className="sticky start-0 z-10 bg-card">{t('reports.columns.kpi')}</TableHeaderCell>
          {weeks.map((week) => (
            <TableHeaderCell
              key={week.id}
              data-week-id={week.id}
              className="px-2 text-end"
              title={week.weekDate}
            >
              {weekShortLabel(week)}
              <span className="block text-[10px] font-normal normal-case">W{String(week.weekNumber).padStart(2, '0')}</span>
            </TableHeaderCell>
          ))}
        </tr>
      </TableHead>
      <tbody>
        {kpis.map((kpi) => (
          <TableRow
            key={kpi.id}
            data-kpi-id={kpi.id}
          >
            <TableCell
              className="sticky start-0 z-10 whitespace-nowrap bg-card py-1.5 font-medium"
              style={{ paddingInlineStart: `${1 + kpi.depth}rem` }}
            >
              <span
                aria-hidden
                className={cn('me-2 inline-block size-2 rounded-full', PILLAR_CLASSES[kpi.pillar].dot)}
              />
              {kpi.name}
            </TableCell>
            {weeks.map((week) => {
              const value = values[`${kpi.id}:${week.id}`];

              return (
                <TableCell
                  key={week.id}
                  data-week-id={week.id}
                  className={cn('whitespace-nowrap px-2 py-1.5 text-end font-mono', value === undefined && 'text-muted-foreground')}
                >
                  {reportValueText(kpi, value)}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </tbody>
    </Table>
  );
};
